<template>
    <section class="mt-6 flex w-full flex-col lg:flex-row">
        <div class="flex w-full items-center justify-between">
            <button
                type="button"
                class="lg:hidden"
                @click="appStore.side = !appStore.side"
            >
                <SvgIcon
                    name="menu"
                    class="h-6 w-6"
                />
            </button>
            <div class="ml-4 mr-auto flex hidden items-center gap-3 sm:flex lg:ml-0 lg:mr-0">
                <span class="inter text-2xl font-medium">{{ headerTitle }}</span>
                <router-link
                    v-if="showReturnToOrderLink"
                    :to="returnToOrderRoute"
                    class="mt-1 pl-4 text-sm font-medium text-brand no-underline hover:opacity-80"
                    @click="setOrderViewActiveTabOnReturn"
                >
                    Return to order
                </router-link>
                <span
                    v-if="order.getOrderRef"
                    class="pt-0.5 text-sm text-muval-gray-2"
                    >#{{ order.getOrderRef }}</span
                >

                <Lozenge
                    v-if="order.data?.state === 'BIN'"
                    class="mt-0.5 bg-muval-gray-5 text-xs text-muval-gray-1"
                    rounded
                >
                    {{ capitalize(order.data?.status_formatted) }}
                </Lozenge>
            </div>
            <div class="relative flex w-fit shrink-0 items-center gap-x-4">
                <RoleGuard :guarded="['ADMIN', 'SALES', 'OPS', 'MANAGER', 'FINANCE', 'OWNER', 'SUPER']">
                    <BookingStatus />
                    <CommandPalette />
                    <router-link
                        class="relative items-center gap-x-1 lg:flex"
                        to="/inbox/calls"
                    >
                        <SvgIcon
                            name="calls"
                            class="size-5 text-muval-gray-1"
                        />
                        <div
                            v-show="badgeCount.phone > 0"
                            class="absolute -right-2.5 -top-2.5"
                        >
                            <span
                                class="flex size-[18px] items-center justify-center rounded-full bg-brand text-xs text-white"
                                v-text="badgeCount.phone"
                            />
                            <span
                                class="absolute top-0 -z-1 flex size-[18px] animate-ping items-center justify-center rounded-full bg-brand text-xs text-white"
                                v-text="' '"
                            />
                        </div>
                    </router-link>
                    <router-link
                        class="relative items-center gap-x-1 lg:flex"
                        to="/inbox/sms"
                    >
                        <SvgIcon
                            name="inbox"
                            class="size-5 text-muval-gray-1"
                        />
                        <div
                            v-show="badgeCount.text > 0"
                            class="absolute -right-2.5 -top-2.5"
                        >
                            <span
                                class="flex size-[18px] items-center justify-center rounded-full bg-brand text-xs text-white"
                                v-text="badgeCount.text"
                            />
                            <span
                                class="absolute top-0 -z-1 flex size-[18px] animate-ping items-center justify-center rounded-full bg-brand text-xs text-white"
                                v-text="' '"
                            />
                        </div>
                    </router-link>
                </RoleGuard>
                <NotificationIcon />
                <!-- Profile dropdown -->
                <ProfileDropDown />
                <UserInactiveModal v-if="!userStore.isMoveproSuperAdminUser" />
                <UserActivityMonitor v-if="!userStore.isMoveproSuperAdminUser" />
            </div>
        </div>
        <div class="ml-0 mr-auto mt-5 flex items-center gap-3 sm:hidden lg:ml-0 lg:mr-0">
            <span class="inter text-2xl font-medium">{{ headerTitle }}</span>
            <router-link
                v-if="showReturnToOrderLink"
                :to="returnToOrderRoute"
                class="mt-1 pl-4 text-sm font-medium text-brand no-underline hover:opacity-80"
                @click="setOrderViewActiveTabOnReturn"
            >
                Return to order
            </router-link>
            <span
                v-if="order.getOrderRef"
                class="pt-0.5 text-sm text-muval-gray-2"
                >#{{ order.getOrderRef }}</span
            >

            <Lozenge
                v-if="order.data?.state === 'BIN'"
                class="mt-0.5 bg-muval-gray-5 text-xs text-muval-gray-1"
                rounded
            >
                {{ capitalize(order.data?.status_formatted) }}
            </Lozenge>
        </div>
    </section>
</template>
<script setup>
import useEmitter from '@plugins/useEmitter';
import { onMounted, onUnmounted } from 'vue';
import { startCase } from 'lodash';
import { useCompanyStore, useOrderStore } from '@store';
import { capitalize } from '@utilities/filters';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

const appStore = useAppStore();
const userStore = useUserStore();
const jobStore = useJobStore();
const tenderStore = useTenderStore();

const breakpoints = useBreakpoints(breakpointsTailwind);
const desktopView = breakpoints.greaterOrEqual('sm');

const badgeCount = reactive({
    text: 0,
    phone: 0,
});

const getInboxCount = async () => {
    const { data: text } = await window.tenantAxios.get('inbox', {
        params: {
            count: 1,
            direction: 'INCOMING',
            seen: 0,
            type: 'TEXT',
            ...(userStore.getTeam != 'NONE' &&
                userStore.getTeam && {
                    team: userStore.getTeam,
                }),
        },
    });

    const { data: phone } = await window.tenantAxios.get('inbox', {
        params: {
            count: 1,
            direction: 'INCOMING',
            seen: 0,
            type: 'PHONE',
            ...(userStore.getTeam != 'NONE' &&
                userStore.getTeam && {
                    team: userStore.getTeam,
                }),
        },
    });

    badgeCount.text = text.count;
    badgeCount.phone = phone.count;
};

const emitter = useEmitter();

onMounted(() => {
    if (!userStore.isOnlyFieldStaff()) {
        getInboxCount();

        let stop = setInterval(getInboxCount, 60000);

        onUnmounted(() => {
            clearInterval(stop);
        });

        // When call/text items are marked as seen, refetch the notification count
        emitter.on('update:seen', getInboxCount);
    }
});

const route = useRoute();

const company = useCompanyStore();
const companyName = computed(() => {
    return company?.data?.name ?? null;
});

const order = useOrderStore();

const headerTitle = computed(() => {
    switch (route.name) {
        case 'Dashboard':
            // return userStore.getFirstName ? `Hi ${userStore.getFirstName}!` : null;
            return 'Dashboard';
        case 'Search':
            return `Search ${userStore.search ?? ''}`;
        // Interstate
        case 'InterstateTripCreate':
            return 'Create interstate trip';
        case 'InterstateTripEdit':
            return 'Edit interstate trip';
        case 'InterstateLaneCreate':
            return 'Create interstate lane';
        case 'InterstateLaneEdit':
            return 'Edit interstate lane';
        // Depots
        case 'DepotsCreate':
            return 'Create depot';
        case 'DepotsEdit':
            return `Edit depot`;
        // Companies
        case 'CompaniesView':
            return companyName.value;
        case 'CompaniesEdit':
            return 'Edit company profile';
        case 'CompaniesPreferences':
            return 'Company preferences';
        // Local
        case 'LocalCreate':
            return 'Create local';
        case 'LocalEdit':
            return 'Edit local';
        // Fleet
        case 'FleetCreate':
            return 'Create vehicle';
        case 'FleetEdit':
            return 'Edit vehicle';
        // Staff
        case 'StaffCreate':
            return 'Create team member';
        case 'StaffEdit':
            return 'Edit team member';
        case 'CompanyStaffEdit':
            return 'Edit team member';
        // SMS & CALL
        case 'InboxCalls':
            return 'Calls';
        case 'InboxSms':
            return 'Inbox';
        // Comms
        case 'CommunicationCreate':
            return 'Create template';
        case 'CommunicationEdit':
            return 'Edit template';
        // Jobs
        case 'JobView':
            return jobStore?.data?.order_ref ? `Job #${jobStore?.data?.order_ref ?? ''}` : '';
        // Quotes
        case 'QuoteView':
            return tenderStore?.tender?.order_ref ? `Quote #${tenderStore?.tender?.order_ref ?? ''}` : '';
        // ICR
        case 'OrderInventoryConditionReport':
            return desktopView.value ? 'Inventory condition report' : 'ICR';
        // Errors
        case '404Page':
            return '';
        default:
            return route?.meta?.title ?? capitalize(startCase(route.name));
    }
});

const showReturnToOrderLink = computed(() => {
    return ['OrderInventoryConditionReport', 'OrderInventory'].includes(route.name ?? '');
});

const returnToOrderRoute = computed(() => {
    return { name: 'OrderView', params: { id: route.params.id } };
});

function setOrderViewActiveTabOnReturn() {
    try {
        const tab = route.name === 'OrderInventoryConditionReport' ? 'ICR' : 'Inventory';
        sessionStorage.setItem('orderView.activeTab', tab);
    } catch {
        // Intentionally ignore sessionStorage errors (e.g., in private mode)
    }
}
</script>
